package com.evoapps.evoshift.ui.user

import com.evoapps.evoshift.ui.util.WeatherForecastCard
import androidx.compose.foundation.relocation.bringIntoViewRequester
import kotlinx.coroutines.launch

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.ui.theme.AnimatedAmountText
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.TopBarAction
import com.evoapps.evoshift.widgets.helpAction
import com.evoapps.evoshift.ui.notifications.notificationsAction
import com.evoapps.evoshift.ui.util.HelpCentreButton
import com.evoapps.evoshift.ui.util.HelpPage
import com.evoapps.evoshift.ui.notifications.NotificationsBell
import com.evoapps.evoshift.ui.kiosk.ScanTabletCodeButton
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.ui.adaptive.ResponsiveColumns
import com.evoapps.evoshift.ui.adaptive.DashboardColumns
import com.evoapps.evoshift.ui.adaptive.PostureAwareScaffold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.adaptive.hoverLift
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Notifications
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.domain.usecase.AtWorkEntry
import com.evoapps.evoshift.viewmodels.Gate
import com.evoapps.evoshift.viewmodels.ManagerDashboardViewModel
import com.evoapps.evoshift.viewmodels.shows
import com.evoapps.evoshift.domain.usecase.shift.ClockStatus
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.widgets.LoadingSwap
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.dashboard_action_inbox
import evoshift.composeapp.generated.resources.dashboard_all_caught_up
import evoshift.composeapp.generated.resources.dashboard_no_issues
import evoshift.composeapp.generated.resources.dashboard_notifications
import evoshift.composeapp.generated.resources.dashboard_open_skeleton_slots
import evoshift.composeapp.generated.resources.dashboard_operational_issues
import evoshift.composeapp.generated.resources.dashboard_pending_review
import evoshift.composeapp.generated.resources.dashboard_profile
import evoshift.composeapp.generated.resources.dashboard_select_workplace
import evoshift.composeapp.generated.resources.dashboard_workplace_switch
import org.jetbrains.compose.resources.stringResource
import com.evoapps.evoshift.location.rememberLocationReader

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ManagerDashboardScreen(
    viewModel: ManagerDashboardViewModel,
    onNavigateToProfile: () -> Unit,
    onNavigateToRoster: () -> Unit,
    onNavigateToRequests: () -> Unit,
    onNavigateToTeam: () -> Unit,
    onSwitchWorkplaceClicked: () -> Unit,
    /** A person in "Today at work": that day of the roster. */
    onOpenAtWork: (AtWorkEntry) -> Unit = {},
    /** SH-121: under a person in "Today at work" (their checklists today). */
    atWorkExtra: @Composable (AtWorkEntry) -> Unit = {},
    /** SH-134: the "to review" list above Today at work (shows nothing when there is nothing). */
    clockReview: @Composable () -> Unit = {},
    /** SH-121: the manager's own checklists today, under their next shift. */
    myChecklists: @Composable () -> Unit = {},
    /**
     * SH-170: "Do zrobienia" (it took over SH-164's inbox card and the open shifts card);
     * [showClockReview] brings today's clock-in review list into view.
     */
    todo: @Composable (showClockReview: () -> Unit) -> Unit = {},
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    // SH-170: the to-do's "Sprawdź" scrolls to the clock-in review list above Today at work.
    val reviewRequester = androidx.compose.runtime.remember { androidx.compose.foundation.relocation.BringIntoViewRequester() }
    val scope = androidx.compose.runtime.rememberCoroutineScope()
    val showClockReview: () -> Unit = { scope.launch { reviewRequester.bringIntoView() } }

    // SH-106: read once at the tap where the workplace checks the location.
    val readLocation = rememberLocationReader()
    if (state.locationNotice) LocationNoticeDialog(viewModel::onLocationNoticeAnswered)
    // SH-158: the clock's answer in the hand.
    ClockResultFeedback(viewModel.clockResults)
    // SH-165: a clock-out well before the planned end is asked about; each one offers Undo.
    state.earlyClockOut?.let { end -> EarlyClockOutDialog(end, viewModel::onEarlyClockOutAnswered) }
    val clockSnackbar = androidx.compose.runtime.remember { androidx.compose.material3.SnackbarHostState() }
    ClockOutUndoSnackbar(state.clockOutUndo, clockSnackbar, viewModel::onUndoClockOut, viewModel::dismissClockOutUndo)

    Scaffold(
        containerColor = Cream,
        snackbarHost = { androidx.compose.material3.SnackbarHost(clockSnackbar) },
        topBar = {
            EvoTopBar(
                showWorkplace = true,
                showMonth = true,
                actions = listOf(
                    helpAction(HelpPage.TODAY_AT_WORK),
                    notificationsAction(),
                    TopBarAction(stringResource(Res.string.dashboard_profile), Icons.Default.Person, onNavigateToProfile, collapsible = false)
                )
            )
        },
    ) { padding ->
        // SH-130: the columns below scroll across the whole screen and centre their content.
        val maxWidth = LocalWindowWidthClass.current.wideContentMaxWidth
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            // SH-188: the first load failed: Try again instead of an empty dashboard.
            val loadError = state.loadError
            if (loadError != null && !state.loaded) {
                com.evoapps.evoshift.widgets.ErrorState(loadError, onRetry = viewModel::retry)
                return@Box
            }
            val clock: @Composable () -> Unit = {
                // Managers work shifts too: the same clock as on the employee dashboard.
                if (state.clock !is ClockStatus.Idle) {
                    ClockInButton(status = state.clock, busy = state.clockBusy, onClick = { viewModel.onClockClicked(readLocation) })
                    // SH-116: while working, start or end a break.
                    BreakButton(status = state.clock, busy = state.clockBusy, onClick = { viewModel.onBreakClicked(readLocation) })
                    ClockNote(state.clockNote, viewModel::clearClockNote)
                }
                // SH-111: or scan the code on the workplace's tablet (to clock in or out; breaks use
                // the break button above, no tablet needed).
                ScanTabletCodeButton(
                    visible = state.clock is ClockStatus.CanClockIn || state.clock is ClockStatus.Working,
                    modifier = Modifier.padding(top = 8.dp)
                )
            }
            val nextShift: @Composable () -> Unit = {
                // SH-247: the navy card's shape until the manager's own shifts are read.
                LoadingSwap(known = state.ownKnown, placeholder = { NextShiftPlaceholder() }) {
                    NextShiftCard(
                        shift = state.nextShift,
                        onClick = onNavigateToRoster,
                        workplaceName = state.workplaceName,
                        modifier = Modifier.hoverLift()
                    )
                }
                myChecklists()
            }
            val error: @Composable () -> Unit = {
                state.error?.let { error ->
                    Text(
                        error.asString(),
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodyMedium,
                        modifier = Modifier.handCursor().clickable { viewModel.clearError() }
                    )
                }
            }
            // Each card only for those who may act on it.
            val caps = state.capabilities
            // SH-170: requests, clock-ins to review, unfilled shifts, availability, publishing: first.
            val todoCard: @Composable () -> Unit = { todo(showClockReview) }
            val todos = listOfNotNull<@Composable () -> Unit>(
                // SH-47: medical exams and OHS training to renew.
                if (caps.shows(Gate.TEAM) && state.staffAlerts.total > 0) {
                    { StaffChecksCard(state.staffAlerts, onClick = onNavigateToTeam, modifier = Modifier.hoverLift()) }
                } else null
            )
            val team: @Composable () -> Unit = {
                LoadingSwap(known = state.loaded, placeholder = { TeamSchedulePlaceholder() }) {
                    TeamScheduleCard(
                        activeEmployees = state.activeEmployees,
                        upcomingEmployees = state.upcomingEmployees,
                        completedEmployees = state.completedEmployees,
                        upcomingDate = state.upcomingDate,
                        onViewDetails = onNavigateToTeam.takeIf { caps.shows(Gate.TEAM) },
                        modifier = Modifier.hoverLift()
                    )
                }
            }
            // Today's staff with their clock-in and clock-out (SH-72).
            val today: (@Composable () -> Unit)? = if (caps.shows(Gate.TODAY_AT_WORK)) {
                {
                    Box(Modifier.bringIntoViewRequester(reviewRequester)) { clockReview() }
                    LoadingSwap(known = state.loaded, placeholder = { TodayAtWorkPlaceholder() }) {
                        TodayAtWorkCard(entries = state.todayAtWork, onOpen = onOpenAtWork, rowExtra = atWorkExtra)
                    }
                }
            } else null
            // SH-226: the forecast for the coming days, where the week is planned (weather on only).
            // SH-247: a placeholder while the setting is not known yet on a dashboard still loading.
            val weather = upcomingWeatherSlot(state.weather, dashboardLoaded = state.loaded)
            // The month's hours, planned against clocked (SH-40).
            val hours: @Composable () -> Unit = {
                LoadingSwap(known = state.loaded, placeholder = { ChartPlaceholder() }) { HoursChartCard(state.hoursByDay) }
            }
            val todayAndHours: @Composable () -> Unit = {
                today?.invoke()
                hours()
            }

            // SH-102: on a foldable the team's day and the charts sit above the hinge (left on a
            // flat, nearly square display); the next shift, the clock and the to-dos below it.
            PostureAwareScaffold(
                controls = {
                    Column(
                        modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).contentWidth(maxWidth).padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        todoCard()
                        nextShift()
                        clock()
                        error()
                        todos.forEach { it() }
                    }
                },
                content = {
                    Column(
                        modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).contentWidth(maxWidth).padding(horizontal = 16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Spacer(modifier = Modifier.height(8.dp))
                        team()
                        weather?.invoke()
                        todayAndHours()
                        Spacer(modifier = Modifier.height(16.dp))
                    }
                },
                normal = {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .verticalScroll(rememberScrollState())
                            .contentWidth(maxWidth)
                            .padding(horizontal = 16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Spacer(modifier = Modifier.height(8.dp))

                        val widthClass = LocalWindowWidthClass.current
                        if (widthClass.isCompact) {
                            todoCard()
                            nextShift()
                            clock()
                            error()
                            ResponsiveColumns(
                                maxColumns = 2,
                                sections = listOfNotNull<@Composable () -> Unit>(
                                    team,
                                    if (todos.isEmpty()) null else {
                                        { Column(verticalArrangement = Arrangement.spacedBy(16.dp)) { todos.forEach { it() } } }
                                    }
                                )
                            )
                            weather?.invoke()
                            todayAndHours()
                        } else {
                            // SH-105: wide windows read across: the manager's own shift, the clock
                            // and the to-dos on the left, the team's day and the month's hours on the
                            // right; laptop-wide ones give the chart a column of its own.
                            val mine: List<@Composable () -> Unit> = listOf(todoCard, nextShift, { Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) { clock() } }, error) + todos
                            val columns = if (widthClass.isLarge) {
                                listOf(mine, listOfNotNull(today, team, weather), listOf(hours))
                            } else {
                                listOf(mine + listOfNotNull(today), listOfNotNull(team, weather, hours))
                            }
                            DashboardColumns(columns)
                        }

                        Spacer(modifier = Modifier.height(48.dp))
                    }
                }
            )

            // SH-247: no spinner over the dashboard: each card shows its placeholder until it is known.
        }
    }
}
